import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, BadgeCheck, MapPin } from 'lucide-react';
import { fetchJson } from '../utils/fetchJson';
import { Spinner } from './ui/Spinner';
import { ExpertCard } from './ExpertCard';

interface TeamMember {
  _id: string;
  firstName: string;
  lastName: string;
  role: string;
  profileImage: string;
  email: string;
  rating?: number;
  isPlaceholder?: boolean;
  social?: {
    twitter?: string;
    linkedin?: string;
    github?: string;
  };
}


const Team: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const token = localStorage.getItem('authToken');

        const data = await fetchJson<{ users?: TeamMember[] } | TeamMember[]>(
          '/api/users?role=fondateur,freelancer,freelancer_admin',
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        
        let members: TeamMember[] = [];
        if (Array.isArray(data)) {
          members = data;
        } else if (data.users) {
          members = data.users;
        } else {
          members = [];
        }

        for (const member of members) {
          if (member.role === 'freelancer' || member.role === 'freelancer_admin') {
            try {
              const ratingData = await fetchJson<{ rating?: number }>(
                `/api/users/${member._id}/rating`,
                token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
              );
              if (typeof ratingData.rating === 'number') {
                member.rating = ratingData.rating;
              }
            } catch {
              // note publique : optionnelle si l’API exige une auth pour cette route
            }
          }
        }

        const MIN_DISPLAY = 3;
        if (members.length < MIN_DISPLAY) {
          const placeholders: TeamMember[] = Array.from(
            { length: MIN_DISPLAY - members.length },
            (_, i) => ({
              _id: `placeholder-${i + 1}`,
              firstName: 'Expert',
              lastName: 'à venir',
              role: 'freelancer',
              profileImage: '',
              email: '',
              isPlaceholder: true,
            })
          );
          setTeamMembers([...members, ...placeholders]);
        } else {
          setTeamMembers(members);
        }
      } catch (err) {
        console.error('[Team] chargement des experts', err);
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  // En cas de chargement
  if (loading) {
    return (
      <section id="team" className="section-spacing bg-[#2F3136]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Notre équipe d'experts</h2>
            <div className="flex justify-center">
              <Spinner size="lg" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="team" className="section-spacing bg-[#2F3136]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#5865F2]/20 text-[#5865F2] mb-4">
            <BadgeCheck size={16} className="mr-1.5" />
            <span className="text-sm font-medium">Des professionnels expérimentés</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center">
            <Users className="mr-2 text-[#5865F2]" size={32} /> Notre équipe&nbsp;<span className="text-[#5865F2]">d'experts</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Nos techniciens ont des années d'expérience dans la réparation de smartphones et d'ordinateurs.
            Confiez vos appareils à des professionnels de confiance.
          </p>
        </motion.div>

        {teamMembers.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member._id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: index * 0.08, ease: 'easeOut' }}
              >
                <ExpertCard {...member} />
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex justify-center mt-8">
          <a
            href="/experts-map"
            className="inline-flex items-center text-[#5865F2] hover:text-[#4752C4] transition-colors"
          >
            <MapPin className="mr-2" size={20} />
            Trouver un expert près de chez vous
          </a>
        </div>
      </div>
    </section>
  );
};

export default Team;